import { useState, createContext, useContext, useEffect } from "react";
import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import { AIIcon, DocumentIcon, LanguageIcon, SubjectIcon } from "@/icons";
import { toast } from "react-toastify";
import AIService from "@/services/ai.service";
import AIImportFlashcardsModal from "./AIImportFlashcardsModal";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

export const GenerateContext = createContext<any | undefined>(undefined);

// Tab Components
const CreateByLanguageTab = ({ deckId }: { deckId?: string }) => {
  const { t } = useTranslation();
  const { setFlashCards } = useContext(GenerateContext);
  const [languageTabData, setLanguageTabData] = useState({
    language: "English",
    focusTopic: "Vocabulary",
    level: "Beginner",
    theme: "Business",
    wordCount: 1
  });
  const [isLoading, setIsLoading] = useState(false);
  const validate = () => {
    if (!deckId) return t("Deck is required.");
    if (!languageTabData.language.trim()) return t("Language is required.");
    if (!languageTabData.focusTopic.trim()) return t("Focus topic is required.");
    if (!languageTabData.level.trim()) return t("Level is required.");
    if (languageTabData.wordCount < 1 || languageTabData.wordCount > 50)
      return t("Card count must be between 1 and 50.");
    return null;
  };
  // Handle Generate
  const handleGenerate = async () => {
    setIsLoading(true);
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      setIsLoading(false);
      return;
    }

    AIService.generateLanguageFlashcards({ ...languageTabData, deckId })
      .then((response: any) => {
        if (response.data) {
          setFlashCards(response.data);
          setIsLoading(false);
        } else {
          toast.error(t("Generate Failed!"));
          setIsLoading(false);
        }
      })
      .catch((errors) => {
        setIsLoading(false);
        toast.error(t("Generate Failed!"));
      });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t("Language")}</Label>
          <input
            type="text"
            value={languageTabData.language}
            onChange={(e) => setLanguageTabData({ ...languageTabData, language: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("English, Japanese, French,...")}
          />
        </div>

        <div>
          <Label>{t("Topic")}</Label>
          <input
            type="text"
            value={languageTabData.focusTopic}
            onChange={(e) => setLanguageTabData({ ...languageTabData, focusTopic: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("Vocabulary, Grammar, Pronunciation,...")}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t("Level")}</Label>
          <input
            type="text"
            value={languageTabData.level}
            onChange={(e) => setLanguageTabData({ ...languageTabData, level: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("Beginner, Intermediate, Advanced,...")}
          />
        </div>

        <div>
          <Label>{t("Theme")}</Label>
          <input
            type="text"
            value={languageTabData.theme}
            onChange={(e) => setLanguageTabData({ ...languageTabData, theme: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("Business, Travel, Academic,...")}
          />
        </div>
      </div>

      <div>
        <Label>{t("Number of Card")}</Label>
        <input
          type="number"
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t("Enter number of cards")}
          min="1"
          max="50"
          value={languageTabData.wordCount}
          onChange={(e) => setLanguageTabData({ ...languageTabData, wordCount: Number(e.target.value) })}
        />
      </div>

      <button onClick={handleGenerate} disabled={isLoading} className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg transition-colors">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5 animate-spin mr-2" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {t("Generating")}...
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <img
              src={AIIcon}
              alt="AI Icon"
              className="w-5 h-5 object-contain filter invert-[29%] sepia-[98%] saturate-[7500%] hue-rotate-[245deg] brightness-[90%] contrast-[90%]"
            />
            {t("Generate")}
          </div>
        )}
      </button>
    </div>
  );
};

const CreateBySubjectTab = ({ deckId }: { deckId?: string }) => {
  const { t } = useTranslation();
  const { setFlashCards } = useContext(GenerateContext);
  const [subjectTabData, setSubjectTabData] = useState({
    subject: "",
    focus: "",
    level: "",
    cardCount: 5
  });
  const [isLoading, setIsLoading] = useState(false);
  const validate = () => {
    if (!deckId) return t("Deck is required.");
    if (!subjectTabData.subject.trim()) return t("Subject is required.");
    if (!subjectTabData.focus.trim()) return t("Focus topic is required.");
    if (!subjectTabData.level.trim()) return t("Level is required.");
    if (subjectTabData.cardCount < 1 || subjectTabData.cardCount > 50)
      return t("Card count must be between 1 and 50.");
    return null;
  };
  // Handle Generate
  const handleGenerate = async () => {
    setIsLoading(true);
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      setIsLoading(false);
      return;
    }

    AIService.generateSubjectFlashcards({ ...subjectTabData, deckId })
      .then((response: any) => {
        if (response.data) {
          setFlashCards(response.data);
          setIsLoading(false);
        } else {
          toast.error("Generate Failed!");
          setIsLoading(false);
        }
      })
      .catch((errors) => {
        setIsLoading(false);
        toast.error("Generate Failed!");
      });
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t("Subject")}</Label>
          <input
            type="text"
            value={subjectTabData.subject}
            onChange={(e) => setSubjectTabData({ ...subjectTabData, subject: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("Mathematics, Materials Science, Technology,...")}
          />
        </div>

        <div>
          <Label>{t("Focus")}</Label>
          <input
            type="text"
            value={subjectTabData.focus}
            onChange={(e) => setSubjectTabData({ ...subjectTabData, focus: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("Key Terms and Definitions, Concepts and Explanations,...")}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t("Level")}</Label>
          <input
            type="text"
            value={subjectTabData.level}
            onChange={(e) => setSubjectTabData({ ...subjectTabData, level: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("Middle School, High School, Graduate,...")}
          />
        </div>

        <div>
          <Label>{t("Number of Card")}</Label>
          <input
            type="text"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            min="1"
            max="50"
            value={subjectTabData.cardCount}
            onChange={(e) => setSubjectTabData({ ...subjectTabData, cardCount: Number(e.target.value) })}
            placeholder={t("Enter number of cards")}
          />
        </div>
      </div>
      <button onClick={handleGenerate} disabled={isLoading} className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg transition-colors">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {t("Generating")}...
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <img
              src={AIIcon}
              alt="AI Icon"
              className="w-5 h-5 object-contain filter invert-[29%] sepia-[98%] saturate-[7500%] hue-rotate-[245deg] brightness-[90%] contrast-[90%]"
            />
            {t("Generate")}
          </div>
        )}
      </button>
    </div>
  );
};

const CreateByDocumentTab = ({ deckId }: { deckId?: string }) => {
  const { setFlashCards } = useContext(GenerateContext);
  const [documentTabData, setDocumentTabData] = useState<any>({
    focus: "",
    cardCount: 5
  });
  const { t } = useTranslation();
  const [file, setFile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const validate = () => {
    if (!deckId) return t("Deck is required.");
    if (!file) return t("File is required.");
    if (!documentTabData.focus.trim()) return t("Focus topic is required.");
    if (documentTabData.cardCount < 1 || documentTabData.cardCount > 50)
      return t("Card count must be between 1 and 50.");
    return null;
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      setIsLoading(false);
      return;
    }
    const fd = new FormData();
    fd.append("deckId", deckId || "");
    for (let key of Object.keys(documentTabData)) {
      if (key === 'focus') {
        fd.append(key, JSON.stringify([documentTabData[key]]));
      } else {
        fd.append(key, documentTabData[key]);
      }
    }
    fd.append("fileDoc", file);
    AIService.generateDocumentFlashcards(fd)
      .then((response: any) => {
        if (response.data) {
          setFlashCards(response.data);
          setIsLoading(false);
        } else {
          toast.error("Generate Failed!");
          setIsLoading(false);
        }
      })
      .catch((errors) => {
        setIsLoading(false);
        toast.error("Generate Failed!");
      });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };
  return (
    <div className="space-y-4">
      <div>
        <Label>{t("Upload Document")}</Label>
        <label
          htmlFor="file-upload"
          className="cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors block"
        >
          <div className="space-y-2">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-gray-600">
              {t("Drag & drop a file or click to select")}
            </p>
            <p className="text-sm text-gray-500">
              {t("DOCX & TXT supported (max 10MB)")}
            </p>
            {file && (
              <p className="text-sm text-green-600 font-medium">
                {file.name}
              </p>
            )}
          </div>
          <input
            id="file-upload"
            type="file"
            className="hidden"
            accept=".docx,.txt"
            onChange={handleFileChange}
          />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t("Focus")}</Label>
          <input
            type="text"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("Key Terms and Definitions, Concepts and Explanations,...")}
            value={documentTabData.focus}
            onChange={(e) => setDocumentTabData({ ...documentTabData, focus: e.target.value })}
          />
        </div>
        <div>
          <Label>{t("Number of Card")}</Label>
          <input
            type="number"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t("Enter number of cards")}
            min="1"
            max="50"
            value={documentTabData.cardCount}
            onChange={(e) => setDocumentTabData({ ...documentTabData, cardCount: Number(e.target.value) })}
          />
        </div>
      </div>
      <button onClick={handleGenerate} disabled={isLoading} className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg transition-colors">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {t("Generating")}...
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <img
              src={AIIcon}
              alt="AI Icon"
              className="w-5 h-5 object-contain filter invert-[29%] sepia-[98%] saturate-[7500%] hue-rotate-[245deg] brightness-[90%] contrast-[90%]"
            />
            {t("Generate")}
          </div>
        )}
      </button>
    </div>
  );
};

export default function CreateFlashcardWithAI() {
  const { deck_id } = useParams();
  const [flashCards, setFlashCards] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('language');
  const { t } = useTranslation();
  const tabs = [
    { id: "language", name: t("By Language"), icon: LanguageIcon },
    { id: "subject", name: t("By Subject"), icon: SubjectIcon },
    { id: "document", name: t("By Document"), icon: DocumentIcon }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'language':
        return <CreateByLanguageTab deckId={deck_id} />;
      case 'subject':
        return <CreateBySubjectTab deckId={deck_id} />;
      case 'document':
        return <CreateByDocumentTab deckId={deck_id} />;
      default:
        return <CreateByLanguageTab deckId={deck_id} />;
    }
  };

  return (
    <div>
      <PageMeta
        title="VIYUME - VIYUME Dashboard"
        description=""
      />
      <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
        <div className="space-y-6">
          <ComponentCard title="">
            <GenerateContext.Provider value={{ flashCards, setFlashCards, deck_id }}>
              <div className="space-y-6">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                      >
                        <img src={tab.icon} alt={tab.name} className="w-5 h-5 object-contain invert-[29%] sepia-[98%] saturate-[7500%] hue-rotate-[245deg] brightness-[90%] contrast-[90%]" />
                        {tab.name}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div>
                  {renderTabContent()}
                </div>
              </div>
              {flashCards.length > 0 && <AIImportFlashcardsModal />}
            </GenerateContext.Provider>
          </ComponentCard>
        </div>
      </div>
    </div>
  );
}